import React from 'react';
import { flushSync } from 'react-dom';

// React 18's createRoot batches every update, including ones made from promise
// callbacks, timers and native event listeners. The legacy ReactDOM.render root
// flushed those synchronously, and much of this app depends on that: state is
// read back immediately after setState, and transient props are set and cleared
// in the same tick. This restores the legacy behaviour until those call sites
// are migrated.

const LIFECYCLE_WARNING = 'flushSync was called from inside a lifecycle method';

let isFlushing = false;

function flushQuietly(update) {
    const consoleError = console.error;
    console.error = function (...args) {
        if (typeof args[0] === 'string' && args[0].indexOf(LIFECYCLE_WARNING) !== -1) {
            return;
        }
        consoleError.apply(console, args);
    };
    try {
        flushSync(update);
    } finally {
        console.error = consoleError;
    }
}

function patchSetState(prototype) {
    const originalSetState = prototype.setState;
    prototype.setState = function (partialState, callback) {
        // Updates raised while a flush is already in progress keep the default
        // batched behaviour, which is what the legacy commit phase did too.
        if (isFlushing) {
            originalSetState.call(this, partialState, callback);
            return;
        }
        isFlushing = true;
        try {
            flushQuietly(() => originalSetState.call(this, partialState, callback));
        } finally {
            isFlushing = false;
        }
    };
}

// PureComponent.prototype receives its own copy of setState when React
// initializes, so patching Component.prototype alone would miss every
// PureComponent subclass.
patchSetState(React.Component.prototype);
patchSetState(React.PureComponent.prototype);
